"use client";
import { useEffect, useRef, useState } from "react";
import dynamic from "next/dynamic";
import { useRouter } from "next/navigation";
import {
  Box,
  Container,
  InputBase,
  Stack,
  Typography,
  Rating
} from "@mui/material";
import {
  LightbulbRounded as FilledIcon,
  LightbulbOutlineRounded as EmptyIcon
} from "@mui/icons-material";
import AgentIcon from "@mui-symbols-material/w400/PsychologyFilledRounded";
import AgentBorderIcon from "@mui-symbols-material/w400/PsychologyOutlined";

import { observer, useStore } from "../../service/mobx";
import useSignInDialog from "../../component/Dialog/dialogs/appWide/Login";
import ChipFilter from "../../component/Chip/Filter";
import ToolTip from "../../component/Tooltip";
import { filterChipScrollSx } from "../search/Filters";
import useDialogUpgrade from "../../component/Dialog/dialogs/appWide/Upgrade";

const ButtonFilled = dynamic(() => import("../../component/Button/Filled"));
const SendOnIcon = dynamic(() =>
  import("@mui/icons-material/ArrowUpwardRounded")
);

function AgentInput({ count }) {
  const { agent } = useStore();

  return agent.threadId ? null : (
    <Container maxWidth="expanded" sx={{ pt: 9 }}>
      <Stack spacing={7} width="100%" alignItems="center">
        <TopSection count={count} />
        <Input count={count} />
      </Stack>
    </Container>
  );
}

export default observer(AgentInput);

const TopSection = ({ count }) => (
  <Stack alignItems="center" spacing={3}>
    <Stack alignItems="center">
      <AgentIcon
        sx={{
          color: "var(--primary-color)",
          height: { compact: 64, expanded: 80 },
          width: { compact: 64, expanded: 80 }
        }}
      />
      <Typography
        fontWeight="700 !important"
        color="var(--secondary-color)"
        typography={{ compact: "headlineMd", expanded: "displayMd" }}
      >
        Agent
      </Typography>
    </Stack>
    <Typography
      component="h1"
      align="center"
      typography={{ compact: "titleLg", expanded: "headlineMd" }}
      fontWeight={{ compact: 600, expanded: 700 }}
      sx={{ textWrap: "balance" }}
    >
      What if you could chat with Arxiv?
    </Typography>
    <Typography
      component="h1"
      align="center"
      typography={{ compact: "titleSm", expanded: "titleMd" }}
      fontWeight={{ compact: 500, expanded: 600 }}
      sx={{ textWrap: "balance" }}
      color="var(--secondary-color)"
    >
      Pose your research questions in plain language. Our research agent dives
      into a knowledge graph of {count.papers} AI papers from top sources like
      Arxiv, NeurIPS, and CVPR, and delivers answers backed by citable studies.
      It's like having a conversation with the entire field of AI.
    </Typography>
  </Stack>
);

function Input({ count }) {
  const [disabled, setDisabled] = useState(false);
  const [userInput, setInput] = useState("");
  const [placeholder, setPlaceholder] = useState("How can Bytez help you?");
  const [selected, setSelected] = useState();
  const [actions] = useState([
    [
      "Ask question",
      "question:",
      `Search the AI knowledge graph, spanning ${count.nodes} entities and ${count.edges} relationships`
    ],
    ["Find models", "search models:", `Search ${count.models} models`],
    ["Browse papers", "search papers:", `Search ${count.papers} AI papers`]
  ]);
  const { agent, user } = useStore();
  const signInDialog = useSignInDialog("Sign in to create more threads");
  const upgradeDialog = useDialogUpgrade("Upgrade to create more threads");
  const router = useRouter();
  const ref = useRef();

  const submitToAgent = async () => {
    let userInputTrimmed = userInput.trim();

    if (agent.maxThreads) {
      if (user.isAnonymous) {
        signInDialog();
      } else {
        upgradeDialog();
      }
    } else if (userInputTrimmed) {
      setDisabled(true);

      if (selected) {
        const [, placeholder] = actions.find(([label]) => label === selected);

        userInputTrimmed = `${placeholder} ${userInputTrimmed}`;
      }

      router.push("/agent");

      try {
        agent.set.running(true);

        await agent.thread.create(userInputTrimmed);
      } finally {
        agent.set.running(false);
      }
    }
  };

  const nothingSelected = selected === undefined;

  useEffect(() => {
    if (userInput === "" && nothingSelected) {
      let rounds = 0;
      let index = 0;
      const placeholders = [
        'I can help you search papers (try "1706.03762" or "attention is all you need")',
        'I can search models (try "deepseek" or "openai/whisper-large-v3-turbo")',
        'I can answer research questions (try "What does the research say about using LSTMs in self-driving cars?")'
      ];

      const id = setInterval(() => {
        const placeholder = placeholders[index++];

        if (placeholder === undefined) {
          index = 0;

          if (++rounds === 2) {
            clearInterval(id);
          }
        }

        setPlaceholder(placeholder || "How can I help you?");
      }, 6e3);

      return () => clearInterval(id);
    }
  }, [userInput, nothingSelected]);

  useEffect(() => {
    setDisabled(agent.running);
  }, [agent.running]);

  return (
    <Box width="100%">
      <Box
        borderRadius="var(--shape-md-round)"
        sx={{
          p: { compact: 1, expanded: 2 },
          border: 1,
          borderColor: "var(--outline-variant)",
          transition: "all 0.5s var(--motion-easing-emphasized)",
          bgcolor: "var(--surface-container-lowest)",
          "&:hover": {
            bgcolor: "var(--surface-container-low)"
          },
          "& button:hover": {
            bgcolor: "var(--surface-container-highest)"
          }
        }}
      >
        <Stack
          position="sticky"
          bottom={0}
          useFlexGap
          spacing={0.5}
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          pl={{ compact: 1, expanded: 0, large: 0 }}
        >
          <InputBase
            ref={ref}
            fullWidth
            autoFocus
            multiline
            maxRows={10}
            minRows={5}
            value={userInput}
            placeholder={placeholder}
            disabled={disabled}
            sx={{
              position: "relative",
              typography: "titleMd",
              color: "var(--surface-on-color)",
              ".Mui-disabled": {
                fontStyle: "italic",
                WebkitTextFillColor: "var(--secondary-on-container)"
              }
            }}
            inputProps={{
              sx: { color: "var(--surface-on-color)" }
            }}
            onKeyDown={event => {
              if (
                event.ctrlKey &&
                event.shiftKey === false &&
                event.key === "Enter"
              ) {
                submitToAgent();
              }
            }}
            onChange={event => setInput(event.target.value)}
          />
          <Box alignSelf="flex-start">
            <ButtonFilled
              disabled={disabled || userInput === ""}
              sx={{ borderRadius: "var(--shape-md-round)" }}
              IconEnd={SendOnIcon}
              onClick={submitToAgent}
            />
          </Box>
        </Stack>
        <Box
          position="relative"
          height={32}
          p={{ compact: 1, expanded: 2 }}
          mr={-1}
          sx={filterChipScrollSx}
        >
          <Stack
            height={32}
            left={0}
            bottom={0}
            pr={1}
            position="absolute"
            direction="row"
            alignItems="center"
            spacing={1}
          >
            {actions.map(([label, placeholder, tooltip]) => (
              <ToolTip key={label} title={tooltip}>
                <ChipFilter
                  label={label}
                  selected={selected === label}
                  onClick={() => {
                    ref.current.querySelector("textarea").focus();

                    setPlaceholder(placeholder);
                    setSelected(selected === label ? undefined : label);
                  }}
                  sx={{
                    height: 28,
                    typography: "labelSm",
                    borderRadius: "var(--shape-sm)",
                    "& p": {
                      typography: "labelMd",
                      fontWeight: 500
                    }
                  }}
                />
              </ToolTip>
            ))}
          </Stack>
        </Box>
      </Box>
      <Proficiency count={count} />
    </Box>
  );
}

function Proficiency({ count }) {
  const items = [
    {
      label: "AI papers",
      stars: 4,
      cutOff: count.mostRecentPaper
    },
    {
      label: "Models",
      stars: 2,
      cutOff: count.mostRecentModel
    }
  ];

  return (
    <Box
      mx={2}
      border={1}
      borderTop={0}
      borderRadius="var(--shape-lg-bottom)"
      overflow="hidden"
      borderColor="var(--outline-variant)"
      bgcolor="var(--surface-container-highest)"
    >
      <Stack direction="row" p={1} spacing={1}>
        <AgentBorderIcon
          sx={{ width: 40, height: 40, color: "var(--secondary-color)" }}
        />
        <Stack>
          <Typography variant="titleMd" color="var(--secondary-color)">
            Agent
          </Typography>
          <Typography variant="bodyMd" sx={{ textWrap: "balance" }}>
            Expertise in AI research papers; basic proficiency with AI models.
          </Typography>
        </Stack>
      </Stack>
      <Stack direction={{ compact: "column", medium: "row" }}>
        {items.map(({ label, stars, cutOff }, index) => (
          <Stack
            key={label}
            flex={1}
            direction="row"
            borderTop={1}
            justifyContent="space-around"
            borderRight={index === 0 ? 1 : 0}
            borderColor="var(--outline-variant)"
          >
            <Stack p={1} spacing={1}>
              <Typography
                variant="labelSm"
                sx={{ textWrap: "balance" }}
                color="var(--secondary-color)"
              >
                Intelligence - {label}
              </Typography>
              <Rating
                readOnly
                size="small"
                value={stars}
                icon={
                  <FilledIcon
                    fontSize="inherit"
                    sx={{ color: "var(--secondary-color)" }}
                  />
                }
                emptyIcon={
                  <EmptyIcon
                    fontSize="inherit"
                    sx={{ color: "var(--secondary-color)" }}
                  />
                }
              />
            </Stack>
            <Stack p={1} spacing={1}>
              <Typography variant="labelSm" color="var(--secondary-color)">
                Knowledge Cutoff
              </Typography>
              <Typography variant="labelMd">
                {cutOff === "0"
                  ? "Today"
                  : cutOff === "1"
                  ? "1 day ago"
                  : `${cutOff} days ago`}
              </Typography>
            </Stack>
          </Stack>
        ))}
      </Stack>
    </Box>
  );
}
